import React, { useMemo } from "react";

import TransferTrackerTimeline from "./TransferTrackerTimeline";

function formatStepTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Receiver-side progress for an incoming transfer accept / reject flow. */
export default function IncomingTransferAcceptSteps({ requestedAt, accepting, rejecting, rejected }) {
  const steps = useMemo(() => {
    const sentTime = formatStepTime(requestedAt);

    if (rejected) {
      return [
        {
          key: "received",
          title: "Offer received",
          detail: sentTime ? `Sent to you on ${sentTime}` : "The sender posted this NAV-locked offer.",
          state: "complete",
        },
        {
          key: "accept",
          title: "You declined",
          detail: "Your rejection was recorded and shared with the sender.",
          state: "complete",
        },
        {
          key: "settled",
          title: "Settlement",
          detail: "No settlement — your wallet was not charged.",
          state: "skipped",
        },
      ];
    }

    if (rejecting) {
      return [
        {
          key: "received",
          title: "Offer received",
          detail: sentTime ? `Sent to you on ${sentTime}` : "The sender posted this NAV-locked offer.",
          state: "complete",
        },
        {
          key: "accept",
          title: "You declined",
          detail: "Recording your rejection and notifying the sender.",
          state: "current",
        },
        {
          key: "settled",
          title: "Settlement",
          detail: "No settlement — this offer was declined.",
          state: "skipped",
        },
      ];
    }

    if (accepting) {
      return [
        {
          key: "received",
          title: "Offer received",
          detail: sentTime ? `Sent to you on ${sentTime}` : "The sender posted this NAV-locked offer.",
          state: "complete",
        },
        {
          key: "accept",
          title: "You accepted",
          detail: "Your confirmation was received.",
          state: "complete",
        },
        {
          key: "settled",
          title: "Settlement",
          detail: "Processing wallet payment and share transfer…",
          state: "current",
        },
      ];
    }

    return [
      {
        key: "received",
        title: "Offer received",
        detail: sentTime ? `Sent to you on ${sentTime}` : "The sender posted this NAV-locked offer.",
        state: "complete",
      },
      {
        key: "accept",
        title: "Your decision",
        detail: "Accept to settle at the locked NAV, or reject with a reason for the sender.",
        state: "current",
      },
      {
        key: "settled",
        title: "Settlement",
        detail: "Shares move to you and prefunded settlement completes when you accept.",
        state: "upcoming",
      },
    ];
  }, [requestedAt, accepting, rejecting, rejected]);

  return <TransferTrackerTimeline steps={steps} ariaLabel="Accept transfer progress" />;
}
